import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Eye, FileText, Trash2 } from "lucide-react";
import { getApiErrorPayload, quotationsAPI } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/lib/utils";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { Loading } from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { HubFilters } from "@/features/sales/hubFilters";
import { confirmAction } from "@/components/ui/confirm-dialog";

const STATUSES = ["draft", "sent", "accepted", "rejected", "expired", "cancelled", "converted"];

const statusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" | "success" | "warning" => {
	switch (status) {
		case "converted":
		case "accepted":
			return "success";
		case "sent":
			return "default";
		case "rejected":
		case "cancelled":
			return "destructive";
		case "expired":
			return "warning";
		default:
			return "secondary";
	}
};

const QuotationsPage: React.FC<{ filters?: HubFilters }> = ({ filters }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { toast } = useToast();
	const currency = useCurrency();

	const [quotations, setQuotations] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [totalPages, setTotalPages] = useState(1);
	const [totalItems, setTotalItems] = useState(0);
	const [statusFilter, setStatusFilter] = useState<string>("");

	// When embedded in the sales hub, the hub toolbar owns the filters
	useEffect(() => {
		if (!filters) return;
		setStatusFilter(filters.status);
		setCurrentPage(1);
	}, [filters]);

	const fetchQuotations = useCallback(async () => {
		try {
			setIsLoading(true);
			const res = await quotationsAPI.getAll(currentPage, itemsPerPage, {
				status: statusFilter || undefined,
			});
			setQuotations(res.data || []);
			setTotalItems(res.total || 0);
			setTotalPages(Math.max(1, Math.ceil((res.total || 0) / itemsPerPage)));
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	}, [currentPage, itemsPerPage, statusFilter, t, toast]);

	useEffect(() => {
		fetchQuotations();
	}, [fetchQuotations]);

	const handleDelete = async (id: number) => {
		if (!(await confirmAction(t("quotations.confirmDelete"), { confirmLabel: t("common.delete") }))) return;
		try {
			await quotationsAPI.delete(id);
			toast({ title: t("common.success"), description: t("quotations.messages.deleted") });
			fetchQuotations();
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		}
	};

	const customerName = (q: any) => {
		const c = q.customer;
		if (!c) return t("quotations.walkIn");
		return [c.firstName, c.lastName].filter(Boolean).join(" ") || c.companyName || c.phone || `#${c.id}`;
	};

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<PageHeading className="mb-2 ">{t("quotations.title")}</PageHeading>
					<p className="text-muted-foreground">{t("quotations.description")}</p>
				</div>
				{!filters && (
					<PermissionGate permission="sales.create">
						<Button onClick={() => navigate("/quotations/new")}>
							<Plus className="h-4 w-4 mx-2" />
							{t("quotations.add.title")}
						</Button>
					</PermissionGate>
				)}
			</div>

			<Card>
				<CardContent className="pt-6">
					{!filters && (
						<div className="flex items-center gap-4 mb-4">
							<Select
								value={statusFilter || "__all__"}
								onValueChange={v => {
									setStatusFilter(v === "__all__" ? "" : v);
									setCurrentPage(1);
								}}
							>
								<SelectTrigger className="w-56">
									<SelectValue placeholder={t("quotations.filterStatus")} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="__all__">{t("quotations.allStatuses")}</SelectItem>
									{STATUSES.map(s => (
										<SelectItem key={s} value={s}>
											{t(`quotations.status.${s}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}

					{isLoading ? (
						<Loading />
					) : quotations.length === 0 ? (
						<div className="text-center py-12 text-muted-foreground">
							<FileText className="h-12 w-12 mx-auto mb-4 opacity-40" />
							<p>{t("quotations.empty")}</p>
						</div>
					) : (
						<>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t("quotations.table.number")}</TableHead>
										<TableHead>{t("quotations.table.customer")}</TableHead>
										<TableHead>{t("quotations.table.date")}</TableHead>
										<TableHead>{t("quotations.table.validUntil")}</TableHead>
										<TableHead>{t("quotations.table.total")}</TableHead>
										<TableHead>{t("quotations.table.status")}</TableHead>
										<TableHead className="text-start">{t("quotations.table.actions")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{quotations.map(q => (
										<TableRow key={q.id}>
											<TableCell className="font-medium">{q.quotationNumber}</TableCell>
											<TableCell>{customerName(q)}</TableCell>
											<TableCell>{q.createdAt ? formatDate(q.createdAt) : "-"}</TableCell>
											<TableCell>{q.validUntil ? formatDate(q.validUntil) : "-"}</TableCell>
											<TableCell>
												<Price value={q.totalAmount} currency={currency} />
											</TableCell>
											<TableCell>
												<Badge variant={statusVariant(q.status)}>
													{t(`quotations.status.${q.status}`)}
												</Badge>
											</TableCell>
											<TableCell className="text-start">
												<div className="flex items-center justify-end gap-2">
													<PermissionGate permission="sales.read">
														<Button
															title={t("common.view")}
															aria-label={t("common.view")}
															size="icon"
															variant="ghost"
															onClick={() => navigate(`/quotations/${q.id}`)}
														>
															<Eye className="h-4 w-4" />
														</Button>
													</PermissionGate>
													{q.status !== "converted" && (
														<PermissionGate permission="sales.delete">
															<Button
																title={t("common.delete")}
																aria-label={t("common.delete")}
																size="icon"
																variant="ghost"
																onClick={() => handleDelete(q.id)}
															>
																<Trash2 className="h-4 w-4 text-destructive" />
															</Button>
														</PermissionGate>
													)}
												</div>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
							<Pagination
								currentPage={currentPage}
								totalPages={totalPages}
								totalItems={totalItems}
								itemsPerPage={itemsPerPage}
								onPageChange={setCurrentPage}
								onPageSizeChange={setItemsPerPage}
								itemName="quotations.pagination.items"
							/>
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
};

export default QuotationsPage;
